import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useTheme } from "@/lib/theme-context";
import PageHeader from "@/components/app/PageHeader";
import StatTile, { SURFACE } from "@/components/app/StatTile";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, AreaChart, Area,
} from "recharts";
import { RefreshCw, Download, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { toast } from "sonner";

const downloadCsv = async (url, filename) => {
  const BASE = process.env.REACT_APP_API_URL || "";
  const res = await fetch(`${BASE}${url}`, { credentials: "include" });
  if (!res.ok) throw new Error("Export failed");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

// Recharts renders colours via inline SVG attributes/props, so they can't be
// themed through Tailwind classes — pick a palette per theme explicitly and
// keep both readable against their own background (this is what was making
// the mint-green slice unreadable on white, and the tooltip box render
// near-black on hover regardless of page theme).
const CHART_PALETTES = {
  light: {
    green: "#059669", amber: "#D97706", red: "#E11D48", blue: "#4F46E5", gray: "#94A3B8",
    grid: "#E2E8F0", axis: "#64748B", legend: "#475569",
    tooltipBg: "#FFFFFF", tooltipBorder: "#E2E8F0", tooltipText: "#0F172A",
  },
  dark: {
    green: "#34D399", amber: "#FBBF24", red: "#FB4B8F", blue: "#818BF4", gray: "#8B96A5",
    grid: "#2A313D", axis: "#8B96A5", legend: "#B4BCC8",
    tooltipBg: "#1B212C", tooltipBorder: "#333C4A", tooltipText: "#F1F5F9",
  },
};

// KPI kept as a local alias so every existing <KPI /> call site below reads
// unchanged — it's the shared StatTile component (also used by Dashboard,
// Invoices, My Tasks, Daily Standup, Burndown) so this page's look stays in
// sync with the rest of the app instead of drifting from its own copy.
const KPI = StatTile;

const ChartCard = ({ title, children, span = 1, actions }) => (
  <div className={`${SURFACE} rounded-xl p-5 ${span === 2 ? "lg:col-span-2" : ""}`}>
    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
      <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{title}</div>
      {actions}
    </div>
    {children}
  </div>
);

const fmtMoney = (n) =>
  n == null ? "—" : new Intl.NumberFormat(undefined, { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);

const Reporting = () => {
  const { theme } = useTheme();
  const palette = CHART_PALETTES[theme] || CHART_PALETTES.light;
  const HEALTH_COLORS = { green: palette.green, amber: palette.amber, red: palette.red };
  const STATUS_COLORS = [palette.blue, palette.green, palette.amber, palette.gray];
  const TOOLTIP_STYLE = {
    contentStyle: {
      background: palette.tooltipBg, border: `1px solid ${palette.tooltipBorder}`,
      borderRadius: 8, color: palette.tooltipText, fontSize: 12,
      boxShadow: theme === "dark" ? "0 8px 24px rgba(0,0,0,0.45)" : "0 8px 24px rgba(15,23,42,0.08)",
    },
    labelStyle: { color: palette.legend },
  };

  const [portfolio, setPortfolio] = useState(null);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [capacitySummary, setCapacitySummary] = useState(null);
  const [timeSummary, setTimeSummary] = useState(null);
  const [teamPerformance, setTeamPerformance] = useState(null);
  const [wip, setWip] = useState(null);
  const [grouped, setGrouped] = useState(null);
  const [groupBy, setGroupBy] = useState("project_id");
  const [groupBy2, setGroupBy2] = useState("priority");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("projects");
  const [revenueReport, setRevenueReport] = useState(null);
  const [marginReport, setMarginReport] = useState(null);
  const [profitLoss, setProfitLoss] = useState(null);
  const [workSummary, setWorkSummary] = useState(null);
  const [timeOffReport, setTimeOffReport] = useState(null);
  const [historyMetric, setHistoryMetric] = useState("margin_pct");
  const [historyPoints, setHistoryPoints] = useState(null);

  const loadGrouped = useCallback(async (gb, gb2) => {
    try {
      const { data } = await api.get(`/reports/grouped-tasks?group_by=${gb}&group_by2=${gb2 || ""}`);
      setGrouped(data);
    } catch {
      toast.error("Failed to load grouped report");
    }
  }, []);

  const loadHistory = useCallback(async (metric) => {
    try {
      const { data } = await api.get(`/reports/history?metric=${metric}&months=6`);
      setHistoryPoints(data.points || []);
    } catch {
      setHistoryPoints(null);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const [p, inv, cap, time, team, wipRes, groupedRes, revRes, marginRes, plRes, workRes, timeOffRes] = await Promise.all([
        api.get("/reports/portfolio"),
        api.get("/invoices?limit=500").catch(() => ({ data: [] })),
        api.get(`/capacity/weekly-grid?week_start=${today}&weeks=8`).catch(() => ({ data: null })),
        api.get("/timesheets/summary").catch(() => ({ data: [] })),
        api.get("/reports/team-performance").catch(() => ({ data: null })),
        api.get("/reports/wip").catch(() => ({ data: null })),
        api.get(`/reports/grouped-tasks?group_by=${groupBy}&group_by2=${groupBy2}`).catch(() => ({ data: null })),
        api.get("/reports/revenue").catch(() => ({ data: null })),
        api.get("/reports/margin").catch(() => ({ data: null })),
        api.get("/reports/profit-loss").catch(() => ({ data: null })),
        api.get("/reports/work-summary").catch(() => ({ data: null })),
        api.get("/leave/report").catch(() => ({ data: null })),  // 403 for non-managers — silently empty
      ]);
      setPortfolio(p.data);
      setTeamPerformance(team.data);
      setWip(wipRes.data);
      setGrouped(groupedRes.data);
      setRevenueReport(revRes.data);
      setMarginReport(marginRes.data);
      setProfitLoss(plRes.data);
      setWorkSummary(workRes.data);
      setTimeOffReport(timeOffRes.data);
      loadHistory("margin_pct");

      // Invoice aggregation. lifecycle_status only ever holds draft/sent/
      // voided — the paid/overdue/partially_paid distinction lives in the
      // computed "status" field the API returns alongside it.
      const invs = p.data?.invoices || inv.data || [];
      const totalRevenue = invs.filter((i) => i.status === "paid").reduce((s, i) => s + (i.total_amount || 0), 0);
      const totalOutstanding = invs.filter((i) => ["sent", "overdue", "partially_paid"].includes(i.status)).reduce((s, i) => s + (i.balance_due || 0), 0);
      const totalDraft = invs.filter((i) => i.status === "draft").reduce((s, i) => s + (i.total_amount || 0), 0);
      const byStatus = ["draft", "sent", "paid", "overdue", "partially_paid", "voided"].map((s) => ({
        name: s, value: invs.filter((i) => i.status === s).length,
      })).filter((d) => d.value > 0);
      setInvoiceStats({ totalRevenue, totalOutstanding, totalDraft, byStatus, count: invs.length });

      // Capacity aggregation
      if (cap.data?.rows?.length) {
        const rows = cap.data.rows;
        const totalAlloc = rows.flatMap((r) => r.cells).reduce((s, c) => s + (c.allocated || 0), 0);
        const totalLogged = rows.flatMap((r) => r.cells).reduce((s, c) => s + (c.logged || 0), 0);
        const overCapCount = rows.filter((r) => r.cells.some((c) => c.over_capacity)).length;
        setCapacitySummary({ totalAlloc, totalLogged, overCapCount, utilisation: totalAlloc > 0 ? Math.round((totalLogged / totalAlloc) * 100) : 0 });
      }

      setTimeSummary(Array.isArray(time.data) ? time.data : []);
    } catch (e) { toast.error("Failed to load report data"); }
    finally { setLoading(false); }
  }, [loadHistory]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!loading) loadHistory(historyMetric); }, [historyMetric]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="px-8 py-16 text-sm text-muted-foreground flex items-center gap-2">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading report data…
      </div>
    );
  }

  const safeEntries = (obj) => obj && typeof obj === "object" ? Object.entries(obj) : [];

  const healthData = safeEntries(portfolio?.health).map(([k, v]) => ({ name: k, value: v }));
  const statusData = safeEntries(portfolio?.status_distribution).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));
  const taskData = safeEntries(portfolio?.task_status).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));
  const raidData = safeEntries(portfolio?.raid_by_type).map(([k, v]) => ({ name: k, value: v }));

  const TABS = ["projects", "invoices", "financials", "capacity", "team", "work", "timeoff", "depth", "trends"];
  const TAB_LABEL = { timeoff: "Time Off" };
  const fmtPct = (n) => n == null ? "—" : `${n}%`;

  const GROUP_FIELD_LABEL = { project_id: "Project", priority: "Priority", status: "Status", assignee: "Assignee", item_type: "Type", entity_id: "Entity" };
  const changeGroupBy = (field, value) => {
    const gb = field === "primary" ? value : groupBy;
    const gb2 = field === "secondary" ? value : groupBy2;
    if (field === "primary") setGroupBy(value); else setGroupBy2(value);
    loadGrouped(gb, gb2);
  };
  const DeltaBadge = ({ pct }) => {
    if (pct == null) return <span className="text-muted-foreground text-xs">—</span>;
    const Icon = pct > 0 ? ArrowUp : pct < 0 ? ArrowDown : Minus;
    const colour = pct > 0 ? "text-emerald-600 dark:text-emerald-400" : pct < 0 ? "text-rose-600 dark:text-rose-400" : "text-muted-foreground";
    return <span className={`inline-flex items-center gap-0.5 text-xs font-mono ${colour}`}><Icon className="w-3 h-3" />{Math.abs(pct)}%</span>;
  };

  const thClass = "text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground";
  const trClass = "border-b border-border last:border-0 hover:bg-slate-50 dark:hover:bg-white/[0.04] transition-colors";
  const selectClass = "bg-background border border-input text-foreground text-xs rounded-md px-2 py-1.5";

  return (
    <div>
      <PageHeader
        eyebrow="Executive reporting"
        title="Portfolio insights"
        description="Live KPIs across projects, revenue, capacity, and people."
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => downloadCsv("/api/tasks/export", "tasks-export.csv").catch(() => toast.error("Export failed"))}
              data-testid="export-tasks-btn"
            >
              <Download className="w-4 h-4 mr-1" /> Export Tasks
            </Button>
            <Button variant="outline" onClick={load} data-testid="refresh-report-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        }
      />

      {/* Tab nav */}
      <div className="border-b border-border px-5 sm:px-8 lg:px-12">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`shrink-0 px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] border-b-2 transition-colors capitalize ${
                tab === t
                  ? "border-violet-600 text-violet-600 dark:border-violet-400 dark:text-violet-400"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`report-tab-${t}`}
            >
              {TAB_LABEL[t] || t}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 sm:px-8 lg:px-12 py-8 space-y-4">
        {tab === "projects" && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KPI label="Total projects" value={portfolio?.total_projects} />
              <KPI label="Tasks tracked" value={portfolio?.total_tasks} />
              <KPI label="Open RAID" value={portfolio?.open_raid} colour="danger" />
              <KPI label="Completion rate" value={`${portfolio?.completion_rate ?? 0}%`} colour="success" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-4">
              <ChartCard title="Health (RAG)">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={healthData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} stroke="none">
                      {healthData.map((e) => <Cell key={e.name} fill={HEALTH_COLORS[e.name] || palette.gray} />)}
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: 11, color: palette.legend }} />
                    <Tooltip {...TOOLTIP_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Project status distribution">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={statusData} barSize={28}>
                    <CartesianGrid stroke={palette.grid} vertical={false} />
                    <XAxis dataKey="name" stroke={palette.axis} fontSize={11} />
                    <YAxis stroke={palette.axis} fontSize={11} allowDecimals={false} />
                    <Tooltip {...TOOLTIP_STYLE} cursor={{ fill: palette.grid, opacity: 0.4 }} />
                    <Bar dataKey="value" fill={palette.blue} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Task velocity by status">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={taskData} barSize={28}>
                    <CartesianGrid stroke={palette.grid} vertical={false} />
                    <XAxis dataKey="name" stroke={palette.axis} fontSize={11} />
                    <YAxis stroke={palette.axis} fontSize={11} allowDecimals={false} />
                    <Tooltip {...TOOLTIP_STYLE} cursor={{ fill: palette.grid, opacity: 0.4 }} />
                    <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                      {taskData.map((e, i) => <Cell key={e.name} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="RAID composition">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={raidData} layout="vertical" barSize={20}>
                    <CartesianGrid stroke={palette.grid} horizontal={false} />
                    <XAxis type="number" stroke={palette.axis} fontSize={11} allowDecimals={false} />
                    <YAxis dataKey="name" type="category" stroke={palette.axis} fontSize={11} width={80} />
                    <Tooltip {...TOOLTIP_STYLE} cursor={{ fill: palette.grid, opacity: 0.4 }} />
                    <Bar dataKey="value" fill={palette.red} radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          </>
        )}

        {tab === "invoices" && invoiceStats && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KPI label="Revenue collected" value={fmtMoney(invoiceStats.totalRevenue)} colour="success" />
              <KPI label="Outstanding" value={fmtMoney(invoiceStats.totalOutstanding)} colour="warning" />
              <KPI label="In draft" value={fmtMoney(invoiceStats.totalDraft)} />
              <KPI label="Total invoices" value={invoiceStats.count} />
            </div>
            <ChartCard title="Invoices by status" span={2}>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={invoiceStats.byStatus} barSize={40}>
                  <CartesianGrid stroke={palette.grid} vertical={false} />
                  <XAxis dataKey="name" stroke={palette.axis} fontSize={12} />
                  <YAxis stroke={palette.axis} fontSize={11} allowDecimals={false} />
                  <Tooltip {...TOOLTIP_STYLE} cursor={{ fill: palette.grid, opacity: 0.4 }} />
                  <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                    {invoiceStats.byStatus.map((e) => {
                      const fill = e.name === "paid" ? palette.green : e.name === "overdue" ? palette.red : e.name === "sent" ? palette.amber : palette.gray;
                      return <Cell key={e.name} fill={fill} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </>
        )}

        {tab === "financials" && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KPI label="Revenue (period)" value={fmtMoney(profitLoss?.revenue)} colour="success" />
              <KPI label="Total cost" value={fmtMoney(profitLoss?.costs?.total)} colour="warning" />
              <KPI label="Gross profit" value={fmtMoney(profitLoss?.gross_profit)} />
              <KPI label="Gross margin" value={fmtPct(profitLoss?.gross_margin_pct)} colour={profitLoss?.gross_margin_pct < 0 ? "danger" : "success"} />
            </div>

            <ChartCard title="Revenue by month (invoiced vs paid)" span={2}>
              {revenueReport?.by_month?.length > 0 ? (
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={revenueReport.by_month} barSize={24}>
                    <CartesianGrid stroke={palette.grid} vertical={false} />
                    <XAxis dataKey="month" stroke={palette.axis} fontSize={11} />
                    <YAxis stroke={palette.axis} fontSize={11} />
                    <Tooltip {...TOOLTIP_STYLE} cursor={{ fill: palette.grid, opacity: 0.4 }} />
                    <Legend wrapperStyle={{ fontSize: 11, color: palette.legend }} />
                    <Bar dataKey="invoiced" fill={palette.blue} name="Invoiced" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="paid" fill={palette.green} name="Paid" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-sm text-muted-foreground py-6 text-center">No invoiced revenue in this window.</div>
              )}
            </ChartCard>

            <ChartCard title="Cost breakdown (period)">
              {profitLoss?.costs ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: "Labor", value: profitLoss.costs.labor },
                        { name: "Expenses", value: profitLoss.costs.expenses },
                        { name: "Vendor bills", value: profitLoss.costs.vendor_bills },
                      ].filter((d) => d.value > 0)}
                      dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} stroke="none"
                    >
                      {[palette.blue, palette.amber, palette.red].map((c) => <Cell key={c} fill={c} />)}
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: 11, color: palette.legend }} />
                    <Tooltip {...TOOLTIP_STYLE} formatter={(v) => fmtMoney(v)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-sm text-muted-foreground py-6 text-center">No cost data in this window.</div>
              )}
            </ChartCard>

            <ChartCard title={`Margin by project${marginReport?.portfolio?.margin_pct != null ? ` — portfolio ${marginReport.portfolio.margin_pct}%` : ""}`} span={2}>
              {marginReport?.projects?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        {["Project", "Revenue", "Labor", "Expenses", "Vendor", "Gross profit", "Margin"].map(h => (
                          <th key={h} className={thClass}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {marginReport.projects.map(p => (
                        <tr key={p.project_id || "unassigned"} className={trClass}>
                          <td className="px-3 py-2.5 text-foreground font-medium">{p.project_name}</td>
                          <td className="px-3 py-2.5 font-mono text-muted-foreground">{fmtMoney(p.revenue)}</td>
                          <td className="px-3 py-2.5 font-mono text-muted-foreground">{fmtMoney(p.labor_cost)}</td>
                          <td className="px-3 py-2.5 font-mono text-muted-foreground">{fmtMoney(p.expenses)}</td>
                          <td className="px-3 py-2.5 font-mono text-muted-foreground">{fmtMoney(p.vendor_cost)}</td>
                          <td className="px-3 py-2.5 font-mono text-foreground">{fmtMoney(p.gross_profit)}</td>
                          <td className={`px-3 py-2.5 font-mono ${p.margin_pct < 0 ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"}`}>{fmtPct(p.margin_pct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground py-6 text-center">No revenue or cost activity in the last 90 days.</div>
              )}
            </ChartCard>
          </>
        )}

        {tab === "capacity" && (
          <>
            {capacitySummary ? (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <KPI label="Allocated hours (8 wks)" value={`${capacitySummary.totalAlloc}h`} />
                  <KPI label="Logged hours (8 wks)" value={`${capacitySummary.totalLogged}h`} colour="success" />
                  <KPI label="Utilisation" value={`${capacitySummary.utilisation}%`} colour={capacitySummary.utilisation >= 90 ? "danger" : "warning"} />
                  <KPI label="Over-capacity people" value={capacitySummary.overCapCount} colour={capacitySummary.overCapCount > 0 ? "danger" : "default"} />
                </div>
                <div className={`${SURFACE} rounded-xl p-6 text-sm text-muted-foreground`}>
                  Capacity drill-down available on the <Link to="/capacity" className="text-violet-600 dark:text-violet-400 hover:underline font-medium">Capacity page</Link>.
                </div>
              </>
            ) : (
              <div className={`${SURFACE} rounded-xl py-12 text-center text-sm text-muted-foreground`}>No capacity data yet.</div>
            )}
          </>
        )}

        {tab === "team" && (
          <>
            {timeSummary?.length > 0 ? (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <KPI label="Team members" value={timeSummary.length} />
                  <KPI label="Total hours (week)" value={`${timeSummary.reduce((s, r) => s + (r.total_hours || 0), 0)}h`} />
                  <KPI label="Approved hours" value={`${timeSummary.reduce((s, r) => s + (r.approved_hours || 0), 0)}h`} colour="success" />
                  <KPI label="Pending approval" value={`${timeSummary.reduce((s, r) => s + (r.pending_hours || 0), 0)}h`} colour="warning" />
                </div>
                <ChartCard title="Hours by team member (this week)" span={2}>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={timeSummary.map((r) => ({ name: r.name || r.user_id, total: r.total_hours || 0, approved: r.approved_hours || 0, pending: r.pending_hours || 0 }))} barSize={20}>
                      <CartesianGrid stroke={palette.grid} vertical={false} />
                      <XAxis dataKey="name" stroke={palette.axis} fontSize={11} />
                      <YAxis stroke={palette.axis} fontSize={11} />
                      <Tooltip {...TOOLTIP_STYLE} cursor={{ fill: palette.grid, opacity: 0.4 }} />
                      <Bar dataKey="approved" stackId="a" fill={palette.green} name="Approved" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="pending" stackId="a" fill={palette.amber} name="Pending" />
                      <Legend wrapperStyle={{ fontSize: 11, color: palette.legend }} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </>
            ) : (
              <div className={`${SURFACE} rounded-xl py-12 text-center text-sm text-muted-foreground`}>No timesheet data this week.</div>
            )}
          </>
        )}

        {tab === "work" && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KPI label="Hours logged (30d)" value={`${workSummary?.total_hours ?? 0}h`} />
              <KPI label="Billable amount (30d)" value={fmtMoney(workSummary?.total_billable_amount)} colour="success" />
              <KPI label="Projects with time" value={workSummary?.by_project?.length ?? 0} />
              <KPI label="People logging time" value={workSummary?.by_person?.length ?? 0} />
            </div>

            <ChartCard title="Hours by project" span={2}
              actions={
                <Button
                  variant="outline" size="sm"
                  onClick={() => downloadCsv("/api/reports/work-summary/export?group_by=project", "work-summary-by-project.csv").catch(() => toast.error("Export failed"))}
                >
                  <Download className="w-3.5 h-3.5 mr-1" /> Export
                </Button>
              }
            >
              {workSummary?.by_project?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        {["Project", "Hours", "Billable"].map(h => (
                          <th key={h} className={thClass}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {workSummary.by_project.map(p => (
                        <tr key={p.project_id || "unassigned"} className={trClass}>
                          <td className="px-3 py-2.5 text-foreground font-medium">{p.project_name}</td>
                          <td className="px-3 py-2.5 font-mono text-muted-foreground">{p.hours}h</td>
                          <td className="px-3 py-2.5 font-mono text-emerald-600 dark:text-emerald-400">{fmtMoney(p.billable_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground py-6 text-center">No time logged in the last 30 days.</div>
              )}
            </ChartCard>

            <ChartCard title="Hours by person">
              {workSummary?.by_person?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        {["Person", "Hours", "Billable"].map(h => (
                          <th key={h} className={thClass}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {workSummary.by_person.map(p => (
                        <tr key={p.user_id} className={trClass}>
                          <td className="px-3 py-2.5 text-foreground font-medium">{p.name}</td>
                          <td className="px-3 py-2.5 font-mono text-muted-foreground">{p.hours}h</td>
                          <td className="px-3 py-2.5 font-mono text-emerald-600 dark:text-emerald-400">{fmtMoney(p.billable_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground py-6 text-center">No time logged in the last 30 days.</div>
              )}
            </ChartCard>
          </>
        )}

        {tab === "timeoff" && (
          <>
            {timeOffReport?.people?.length > 0 ? (
              <ChartCard title={`Time off — ${timeOffReport.year}`} span={2}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        {["Person", "Annual used / allocated", "Sick used / allocated", "Taken YTD", "Upcoming"].map(h => (
                          <th key={h} className={thClass}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {timeOffReport.people.map(p => (
                        <tr key={p.user_id} className={trClass}>
                          <td className="px-3 py-2.5 text-foreground font-medium">{p.name}</td>
                          <td className="px-3 py-2.5 font-mono text-muted-foreground">{p.annual.used}d / {p.annual.allocated}d</td>
                          <td className="px-3 py-2.5 font-mono text-muted-foreground">{p.sick.used}d / {p.sick.allocated}d</td>
                          <td className="px-3 py-2.5 font-mono text-foreground">{p.taken_days_ytd}d</td>
                          <td className="px-3 py-2.5 font-mono text-amber-600 dark:text-amber-400">{p.upcoming_days}d</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </ChartCard>
            ) : (
              <div className={`${SURFACE} rounded-xl py-12 text-center text-sm text-muted-foreground`}>
                No cross-team time-off data — this report is manager-only (owner/admin).
              </div>
            )}
          </>
        )}

        {tab === "depth" && (
          <>
            {/* Team performance */}
            <ChartCard title="Team performance (last 30 days)">
              {teamPerformance?.people?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        {["Person", "Completed", "Created", "Avg cycle (days)", "On-time rate", "Hours logged"].map(h => (
                          <th key={h} className={thClass}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {teamPerformance.people.map(p => (
                        <tr key={p.user_id} className={trClass}>
                          <td className="px-3 py-2.5 text-foreground font-medium">{p.name}</td>
                          <td className="px-3 py-2.5 text-emerald-600 dark:text-emerald-400 font-mono">{p.tasks_completed}</td>
                          <td className="px-3 py-2.5 text-muted-foreground font-mono">{p.tasks_created}</td>
                          <td className="px-3 py-2.5 text-muted-foreground font-mono">{p.avg_cycle_time_days ?? "—"}</td>
                          <td className="px-3 py-2.5 font-mono text-foreground">{p.on_time_rate != null ? `${p.on_time_rate}%` : "—"}</td>
                          <td className="px-3 py-2.5 text-muted-foreground font-mono">{p.hours_logged}h</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground py-6 text-center">No task activity in this window.</div>
              )}
            </ChartCard>

            {/* WIP report */}
            <ChartCard title={`Work in progress${wip ? ` — ${wip.total_wip} items, ${wip.stale_count} stale (${wip.stale_after_days}+ days untouched)` : ""}`}>
              {wip?.projects?.length > 0 ? (
                <div className="space-y-4">
                  {wip.projects.map(g => (
                    <div key={g.project_id || "none"}>
                      <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-2">
                        <span className="font-medium text-foreground">{g.project_name}</span>
                        {g.stale_count > 0 && <span className="text-rose-600 dark:text-rose-400 font-mono">{g.stale_count} stale</span>}
                      </div>
                      <div className="divide-y divide-border border border-border rounded-lg overflow-hidden">
                        {g.items.map(it => (
                          <div key={it.task_id} className="flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-white/[0.04] transition-colors">
                            <span className="text-foreground truncate">{it.title}</span>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{it.status.replace("_", " ")}</span>
                              <span className={`text-xs font-mono ${it.is_stale ? "text-rose-600 dark:text-rose-400" : "text-muted-foreground"}`}>
                                {it.age_days != null ? `${it.age_days}d` : "—"}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground py-6 text-center">Nothing in progress right now.</div>
              )}
            </ChartCard>

            {/* Double grouping + period comparison */}
            <ChartCard title="Grouped task report" span={2}>
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <label className="text-xs text-muted-foreground">Group by</label>
                <select value={groupBy} onChange={(e) => changeGroupBy("primary", e.target.value)} className={selectClass}>
                  {Object.entries(GROUP_FIELD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <label className="text-xs text-muted-foreground">then by</label>
                <select value={groupBy2} onChange={(e) => changeGroupBy("secondary", e.target.value)} className={selectClass}>
                  <option value="">(none)</option>
                  {Object.entries(GROUP_FIELD_LABEL).filter(([k]) => k !== groupBy).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>

              {grouped?.period_comparison && (
                <div className="grid grid-cols-2 gap-4 mb-5">
                  <div className="bg-violet-50 dark:bg-violet-500/10 border border-violet-100 dark:border-violet-400/20 rounded-lg px-4 py-3">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Tasks created (vs prior period)</div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="font-mono text-xl text-foreground">{grouped.period_comparison.current.created}</span>
                      <DeltaBadge pct={grouped.period_comparison.created_delta_pct} />
                    </div>
                  </div>
                  <div className="bg-violet-50 dark:bg-violet-500/10 border border-violet-100 dark:border-violet-400/20 rounded-lg px-4 py-3">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Tasks completed (vs prior period)</div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="font-mono text-xl text-foreground">{grouped.period_comparison.current.completed}</span>
                      <DeltaBadge pct={grouped.period_comparison.completed_delta_pct} />
                    </div>
                  </div>
                </div>
              )}

              {grouped?.groups?.length > 0 ? (
                <div className="space-y-3">
                  {grouped.groups.map(g => (
                    <div key={g.key} className="border border-border rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 bg-violet-50 dark:bg-violet-500/10">
                        <span className="text-sm text-foreground font-medium">{g.label}</span>
                        <span className="text-xs font-mono text-muted-foreground">{g.count} tasks · {g.estimated_hours}h est.</span>
                      </div>
                      {g.sub.length > 0 && (
                        <div className="divide-y divide-border">
                          {g.sub.map(s => (
                            <div key={s.key} className="flex items-center justify-between px-3 py-1.5 text-xs pl-6">
                              <span className="text-muted-foreground">{s.label}</span>
                              <span className="font-mono text-muted-foreground">{s.count} tasks · {s.estimated_hours}h</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground py-6 text-center">No tasks to group yet.</div>
              )}
            </ChartCard>
          </>
        )}

        {tab === "trends" && (
          <>
            <ChartCard
              title="Metric trend (daily snapshots)"
              span={2}
              actions={
                <div className="flex items-center gap-2">
                  <select
                    value={historyMetric}
                    onChange={(e) => setHistoryMetric(e.target.value)}
                    className={selectClass}
                  >
                    <option value="margin_pct">Margin %</option>
                    <option value="revenue">Revenue</option>
                    <option value="utilization_pct">Utilization %</option>
                    <option value="wip_count">WIP count</option>
                  </select>
                  <Button
                    variant="outline" size="sm"
                    onClick={async () => {
                      try { await api.post("/reports/history/snapshot-now"); toast.success("Snapshot taken"); loadHistory(historyMetric); }
                      catch { toast.error("Failed to take snapshot"); }
                    }}
                  >
                    Snapshot now
                  </Button>
                </div>
              }
            >
              {historyPoints?.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={historyPoints}>
                    <CartesianGrid stroke={palette.grid} vertical={false} />
                    <XAxis dataKey="date" stroke={palette.axis} fontSize={11} />
                    <YAxis stroke={palette.axis} fontSize={11} />
                    <Tooltip {...TOOLTIP_STYLE} />
                    <Line type="monotone" dataKey="value" stroke={palette.blue} strokeWidth={2.5} dot={{ r: 3, fill: palette.blue, strokeWidth: 0 }} activeDot={{ r: 5 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-sm text-muted-foreground py-6 text-center">
                  No history yet — snapshots are taken daily, or click "Snapshot now" to seed the first one.
                </div>
              )}
            </ChartCard>
          </>
        )}
      </div>
    </div>
  );
};

export default Reporting;
